import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Plus, 
  Trash2, 
  Send, 
  CheckCircle, 
  Clock, 
  Video,
  LogOut,
  ExternalLink,
  User
} from 'lucide-react';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [books, setBooks] = useState([]);
  
  const authorId = localStorage.getItem('author_id');
  const authorEmail = localStorage.getItem('author_email');

  useEffect(() => {
    if (!authorId) {
      toast.error('Please login to access your dashboard');
      navigate('/author-login');
      return;
    }
    fetchOrders();
  }, [authorId, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/author/${authorId}/orders`);
      setOrders(response.data);
      
      // Set active order to the first pending one
      const pendingOrder = response.data.find(o => o.order_status === 'pending_details');
      if (pendingOrder) {
        setActiveOrder(pendingOrder);
        initializeBooks(pendingOrder.package_id);
      } else if (response.data.length > 0) {
        setActiveOrder(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load your orders');
    } finally {
      setLoading(false);
    }
  };

  const initializeBooks = (packageId) => {
    const maxBooks = getMaxBooks(packageId);
    setBooks([createEmptyBook()]);
  };

  const getMaxBooks = (packageId) => {
    const limits = {
      '2_books': 2,
      '5_books': 5,
      '9_books': 9,
      'trailer': 1,
      'excerpt': 1
    };
    return limits[packageId] || 1;
  };

  const createEmptyBook = () => ({
    id: Date.now(),
    title: '',
    author_name: '',
    amazon_link: '',
    description: ''
  });

  const addBook = () => {
    if (!activeOrder) return;
    const maxBooks = getMaxBooks(activeOrder.package_id);
    if (books.length < maxBooks) {
      setBooks([...books, createEmptyBook()]);
    } else {
      toast.error(`Maximum ${maxBooks} books allowed for this package`);
    }
  };

  const removeBook = (id) => {
    if (books.length > 1) {
      setBooks(books.filter(book => book.id !== id));
    }
  };

  const updateBook = (id, field, value) => {
    setBooks(books.map(book => 
      book.id === id ? { ...book, [field]: value } : book
    ));
  };

  const handleSubmit = async () => {
    if (!activeOrder) return;

    // Validate books
    const validBooks = books.filter(book => book.title && book.amazon_link);
    
    if (validBooks.length === 0) {
      toast.error('Please add at least one book with title and Amazon link');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/author/orders/${activeOrder.id}/submit-books`, {
        order_id: activeOrder.id,
        books: validBooks.map(({ id, ...book }) => book)
      });

      toast.success('Book details submitted successfully!');
      fetchOrders();
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit book details');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('author_id');
    localStorage.removeItem('author_email');
    localStorage.removeItem('author_order_id');
    localStorage.removeItem('author_token');
    navigate('/author-login');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_details': { label: 'Awaiting Book Details', color: 'bg-yellow-100 text-yellow-800' },
      'submitted': { label: 'Submitted', color: 'bg-blue-100 text-blue-800' },
      'in_progress': { label: 'In Production', color: 'bg-purple-100 text-purple-800' },
      'completed': { label: 'Completed', color: 'bg-green-100 text-green-800' }
    };
    const config = statusConfig[status] || { label: status, color: 'bg-slate-100 text-slate-800' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <BookOpen className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-xl font-semibold text-slate-800">Author Dashboard</h1>
                <p className="text-sm text-slate-500">{authorEmail}</p>
              </div>
            </div>
            <Button onClick={handleLogout} variant="outline" className="flex items-center">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Orders List */}
          <div className="lg:col-span-1">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Your Orders</h2>
              
              {orders.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No orders yet</p>
              ) : (
                <div className="space-y-3">
                  {orders.map(order => (
                    <div
                      key={order.id}
                      onClick={() => {
                        setActiveOrder(order);
                        if (order.order_status === 'pending_details') {
                          initializeBooks(order.package_id);
                        } else {
                          setBooks(order.books || []);
                        }
                      }}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        activeOrder?.id === order.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-slate-200 hover:border-purple-300'
                      }`}
                      data-testid={`order-${order.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-slate-800 text-sm">{order.package_title}</p>
                        <p className="font-semibold text-purple-600">£{order.package_price}</p>
                      </div>
                      {getStatusBadge(order.order_status)}
                      
                      {order.order_status === 'completed' && order.video_url && (
                        <div className="mt-2 pt-2 border-t">
                          <a
                            href={order.video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-purple-600 hover:underline flex items-center"
                          >
                            <Video className="w-3 h-3 mr-1" />
                            Download Video
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 pt-6 border-t">
                <a
                  href="/book-video-reviews-for-authors"
                  className="text-purple-600 hover:underline text-sm flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Order Another Package
                </a>
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeOrder ? (
              <>
                {/* Order Details */}
                <Card className="p-6 mb-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white/80 text-sm">Current Order</p>
                      <h2 className="text-2xl font-bold mb-2">{activeOrder.package_title}</h2>
                      {getStatusBadge(activeOrder.order_status)}
                    </div>
                    <div className="text-right">
                      <p className="text-white/80 text-sm">Order ID</p>
                      <p className="font-mono text-sm">{activeOrder.id.slice(0, 8)}...</p>
                    </div>
                  </div>
                </Card>

                {/* Book Submission Form */}
                {activeOrder.order_status === 'pending_details' && (
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-purple-600" />
                      Submit Your Book Details
                    </h3>
                    <p className="text-slate-600 mb-6">
                      Add up to {getMaxBooks(activeOrder.package_id)} book(s) for your video review.
                      Each book needs a title and purchase link.
                    </p>

                    <div className="space-y-6">
                      {books.map((book, index) => (
                        <div
                          key={book.id}
                          className="p-4 border border-slate-200 rounded-lg relative"
                        >
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="font-medium text-slate-800">
                              Book {index + 1}
                            </h4>
                            {books.length > 1 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeBook(book.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>

                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                Book Title *
                              </label>
                              <Input
                                value={book.title}
                                onChange={(e) => updateBook(book.id, 'title', e.target.value)}
                                placeholder="Enter book title"
                                data-testid={`book-title-${index}`}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                Author Name
                              </label>
                              <Input
                                value={book.author_name}
                                onChange={(e) => updateBook(book.id, 'author_name', e.target.value)}
                                placeholder="Author's name"
                                data-testid={`book-author-${index}`}
                              />
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Amazon/Purchase Link *
                            </label>
                            <Input
                              value={book.amazon_link}
                              onChange={(e) => updateBook(book.id, 'amazon_link', e.target.value)}
                              placeholder="https://www.amazon.com/..."
                              data-testid={`book-link-${index}`}
                            />
                          </div>

                          <div className="mt-4">
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Brief Description (Optional)
                            </label>
                            <textarea
                              value={book.description}
                              onChange={(e) => updateBook(book.id, 'description', e.target.value)}
                              placeholder="Brief description of the book..."
                              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                              rows={2}
                              data-testid={`book-description-${index}`}
                            />
                          </div>
                        </div>
                      ))}

                      {books.length < getMaxBooks(activeOrder.package_id) && (
                        <Button
                          variant="outline"
                          onClick={addBook}
                          className="w-full border-dashed"
                          data-testid="add-book-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Another Book
                        </Button>
                      )}

                      <Button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6"
                        data-testid="submit-books-btn"
                      >
                        {submitting ? (
                          <span className="flex items-center">
                            <span className="spinner mr-2"></span>
                            Submitting...
                          </span>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Submit Book Details
                          </>
                        )}
                      </Button>
                    </div>
                  </Card>
                )}

                {/* Submitted Order View */}
                {activeOrder.order_status !== 'pending_details' && (
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                      {activeOrder.order_status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                      ) : (
                        <Clock className="w-5 h-5 mr-2 text-purple-600" />
                      )}
                      {activeOrder.order_status === 'completed' ? 'Order Completed' : 'Order In Progress'}
                    </h3>

                    {/* Submitted Books */}
                    {activeOrder.books && activeOrder.books.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-medium text-slate-700 mb-3">Submitted Books:</h4>
                        <div className="space-y-3">
                          {activeOrder.books.map((book, idx) => (
                            <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                              <p className="font-medium text-slate-800">{book.title}</p>
                              {book.author_name && (
                                <p className="text-sm text-slate-600">by {book.author_name}</p>
                              )}
                              <a
                                href={book.amazon_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-purple-600 hover:underline flex items-center mt-1"
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                View on Amazon
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Completed Order - Video Download */}
                    {activeOrder.order_status === 'completed' && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-2">Your Video is Ready!</h4>
                        <div className="space-y-2">
                          {activeOrder.video_url && (
                            <a
                              href={activeOrder.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-green-700 hover:underline"
                            >
                              <Video className="w-4 h-4 mr-2" />
                              Download MP4 Video
                            </a>
                          )}
                          {activeOrder.youtube_link && (
                            <a
                              href={activeOrder.youtube_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-green-700 hover:underline"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              View on YouTube
                            </a>
                          )}
                        </div>
                      </div>
                    )}

                    {/* In Progress Message */}
                    {(activeOrder.order_status === 'submitted' || activeOrder.order_status === 'in_progress') && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-blue-800">
                          {activeOrder.order_status === 'submitted' 
                            ? 'Your book details have been received. Our team will start working on your video review shortly.'
                            : 'Your video review is currently being produced. You will receive an email when it\'s ready.'}
                        </p>
                      </div>
                    )}
                  </Card>
                )}
              </>
            ) : (
              <Card className="p-8 text-center">
                <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-xl font-semibold text-slate-800 mb-2">No Orders Yet</h3>
                <p className="text-slate-600 mb-4">
                  Order your first video book review to get started.
                </p>
                <a href="/book-video-reviews-for-authors">
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                    Browse Packages
                  </Button>
                </a>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorDashboard;
